import { omit } from "../../../utils/lodash";
import {
    buildPage,
    next,
    branchToRelative,
    branchToAbsolute,
    pageReady,
    previous,
    branchToBlock,
    setPage,
    getCurrentBlock,
    hasNextBlock,
    nextBlock,
    getCurrentLoop,
    updateNavigationButtons,
    blockWillChange,
    buildPageFromServer,
    getServerCurrentQuestions,
} from "./navigationUtilities";
import { NavigationState } from "../../../models/states/NavigationState";
import { DispositionCode } from "../../../models/enums/DispositionCode";
import { TerminationAction } from "../../../models/enums/TerminationAction";
import { InterviewState } from "../../../models/states/InterviewState";
import {
    getRandomizedBlocks,
    getRandomizedQuestionIdByName,
    getRandomizedQuestionIdByVariable,
} from "../../../randomization/Randomization";
import { __isQuestionVisible__, __isBlockVisible__ } from "../../../expressions/parser";
import { ApiBroadcaster } from "../../middleware/apiConnector";
import {
    getTerminationOptions,
    getBlock,
    getQuestionWithName,
    getQuestionWithId,
} from "../../../models/utilities/questionnaireUtilities";
import { QuestionnaireModel } from "../../../models/QuestionnaireModel";
import { ActionTypes } from "../../actions/types";
import {
    NAVIGATION_PAGE_READY,
    NAVIGATION_COMPLETION_QUESTION_REACHED,
    NAVIGATION_BRANCH_TO,
    NAVIGATION_NEXT_PAGE,
    NAVIGATION_PREVIOUS_PAGE,
    NAVIGATION_PREVIEW_QUESTION,
    NAVIGATION_RESET,
    NAVIGATION_DISPLAY_MESSAGE,
    NAVIGATION_QUIT,
    NAVIGATION_SHORTCUT,
    SET_LOOP_ROW,
    SET_NAVIGATION_ENABLED,
    SERVER_PAGE_CHANGED,
    REFRESH_CURRENT_PAGE_QUESTIONS,
} from "../../actions";
import { BranchToOptions } from "../../../models/BranchToOptions";
import { QuitOptions } from "../../../models/QuitOptions";
import { translate } from "../../../models/utilities/translationUtilities";
import { Piping } from "../../../piping";
import { NavigationResultPageModel } from "../../../models/NavigationResultPageModel";
import { isNullOrEmpty } from "../../../utils";

const hasQuestions = (state: NavigationState, interview: InterviewState) =>
    state.currentPage.questions.some((q) => __isQuestionVisible__(q, interview));

const previousPage = (state: NavigationState, interview: InterviewState) => {
    // Repeat until the page has visible questions.
    let newState = state;
    do {
        // Get the previous page.
        newState = previous(newState);
        // If we got the same page, stop looping.
        if (state === newState) break;
    } while (!hasQuestions(newState, { ...interview, navigation: newState }));
    if (state.exitTo != null) {
        while (
            newState.blockIndex === state.blockIndex &&
            getCurrentLoop(newState) === getCurrentLoop(state)
        ) {
            if (newState.questionIndex >= state.questionIndex) {
                const prevState = previous(newState);
                if (prevState === newState) break;
                newState = prevState;
            } else {
                newState.exitTo = state.exitTo;
                break;
            }
        }
    }
    // Clear current question name on server since we do not retrieve this information when navigating back
    return {
        ...newState,
    };
};

const nextPage = (state: NavigationState, interview: InterviewState) => {
    // Repeat until the page has visible questions.
    let newState = state;
    do {
        // Get the next page.
        const nextState = next(newState);
        // If we got the same page, we have reached the end of the survey.
        if (newState === nextState) {
            if (state.exitTo != null) {
                return setPage(state, state.exitTo, true);
            }
            return state;
        }
        newState = nextState;
        if (blockWillChange(state, newState)) {
            // Skip blocks with logic.
            while (!__isBlockVisible__(getCurrentBlock(newState), interview)) {
                // If we have another block, skip this block.
                if (hasNextBlock(newState)) {
                    newState = nextBlock(newState);
                    continue;
                }
                if (state.exitTo != null) {
                    return setPage(state, state.exitTo, true);
                }
                // Quit the survey since no blocks are left.
                return state;
            }
        }
        // Build the page.
        newState = buildPage(newState, interview, state.exitTo);
    } while (!hasQuestions(newState, { ...interview, navigation: newState }));

    if (
        state.exitTo != null &&
        (newState.blockIndex !== state.blockIndex ||
            getCurrentLoop(newState) !== getCurrentLoop(state))
    ) {
        return setPage(state, state.exitTo, true);
    }

    newState.exitTo = state.exitTo;
    return newState;
};

export const completionQuestionReached = (state: NavigationState) => ({
    ...state,
    completionQuestionReached: true,
});

const branchTo = (
    state: NavigationState,
    questionnaire: QuestionnaireModel,
    branchToOptions: BranchToOptions,
    interview: InterviewState,
) => {
    // Exit the survey if exitSurvey is true.
    if (branchToOptions.exitSurvey) {
        ApiBroadcaster(null).quit(interview, DispositionCode.Completed);
        return state;
    }
    // Exit to the next page if exitToNextQuestion is true.
    const exitTo = branchToOptions.exitToNextQuestion ? state.currentPage : undefined;

    const { origin, destination } = branchToOptions;
    const isRelativeBranching =
        destination.startsWith("+") || !Number.isNaN(parseInt(destination, 10));
    const isBlockBranching =
        destination.startsWith("block:") && destination.length > "block:".length;

    // Execute the branching.
    let branchState = state;
    if (isRelativeBranching) {
        branchState = branchToRelative(state, questionnaire, origin, parseInt(destination, 10));
        // Skip blocks with logic only on relative if it is the first question of the block
        while (
            branchState.questionIndex === 0 &&
            !__isBlockVisible__(getCurrentBlock(branchState), interview)
        ) {
            // If we have another block, skip this block.
            if (hasNextBlock(branchState)) {
                branchState = nextBlock(branchState);
                continue;
            }
            if (branchState.exitTo != null) {
                return setPage(branchState, branchState.exitTo, true);
            }
            // Quit the survey since no blocks are left.
            return branchState;
        }
    } else if (isBlockBranching) {
        branchState = branchToBlock(state, questionnaire, destination.substr("block:".length));
    } else {
        branchState = branchToAbsolute(state, questionnaire, destination);
    }

    // If the branching failed, do not build the page.
    if (state === branchState) {
        return state;
    }

    // Return the new page.
    return buildPage(branchState, interview, exitTo);
};

const shortcut = (
    state: NavigationState,
    blockName: string,
    exitToNextQuestion: boolean,
    interview: InterviewState,
) => {
    let exitTo = state.currentPage;
    if (state.exitTo != null) exitTo = state.exitTo;
    else if (exitToNextQuestion) {
        let tmpState = next(state);
        exitTo = null;
        while (!__isBlockVisible__(getCurrentBlock(tmpState), interview)) {
            // If we have another block, skip this block.
            if (hasNextBlock(tmpState)) {
                tmpState = nextBlock(tmpState);
                continue;
            }
            exitTo = state.currentPage;
            break;
        }
        if (exitTo == null) {
            tmpState = buildPage(tmpState, interview);
            exitTo = tmpState.currentPage;
        }
    }
    state = branchToBlock(state, interview.questionnaire, blockName);
    state = buildPage(state, interview, exitTo);
    state = updateNavigationButtons(state);
    return state;
};

const setEnabled = (state: NavigationState, enabled: boolean) => ({
    ...state,
    disabled: !enabled,
});

const setLoopRow = (
    state: NavigationState,
    questionnaire: QuestionnaireModel,
    blockName: string,
    row: number,
) => {
    const block = getBlock(questionnaire, blockName);
    // If block is not found, do nothing.
    if (block === undefined) return state;
    // If row is invalid, do nothing.
    if (row < 1) return state;
    // If the row is 1, we don't need to store it.
    if (row === 1)
        return {
            ...state,
            loopByBlockName: omit(state.loopByBlockName, blockName),
        };
    // Else, save the current loop to the state.
    const { currentPage } = state;
    if (currentPage) currentPage.loopIndex = row;
    return {
        ...state,
        currentPage,
        loopByBlockName: {
            ...state.loopByBlockName,
            [blockName]: row,
        },
    };
};

const reset = (
    state: NavigationState,
    questionnaire: QuestionnaireModel,
    interview: InterviewState,
) => {
    const randomizedBlocks = getRandomizedBlocks(
        interview.respondent,
        questionnaire,
        interview.language,
    );
    state = {
        ...state,
        currentPage: undefined,
        randomizedBlocks,
        randomizedQuestionIdByName: getRandomizedQuestionIdByName(randomizedBlocks),
        randomizedQuestionIdByVariable: getRandomizedQuestionIdByVariable(randomizedBlocks),
        history: [],
        questionIndex: -1,
        loopByBlockName: {},
        disabled: false,
        hasNextPage: false,
        hasPreviousPage: false,
        blockIndex: 0,
        message: undefined,
    };
    state = nextPage(state, interview);
    return state;
};

const displayMessage = (state: NavigationState, message: string) => ({
    ...state,
    message,
});

const navigationQuit = (
    state: NavigationState,
    interview: InterviewState,
    questionnaire: QuestionnaireModel,
    dispositionCode: DispositionCode,
    options: QuitOptions,
) => {
    const terminationOptions = getTerminationOptions(
        questionnaire,
        interview.language,
        dispositionCode,
    );

    const message = !isNullOrEmpty(options?.message)
        ? options?.message
        : terminationOptions.message;

    const shouldDisplayMessage =
        options?.terminationSurveyAction === TerminationAction.DisplayMessage ||
        (!isNullOrEmpty(terminationOptions.message) &&
            options?.terminationSurveyAction !== TerminationAction.RedirectToUrl);

    if (shouldDisplayMessage) {
        const defaultMessage = translate(questionnaire, `TerminationMessage`, interview.language);
        return displayMessage(state, message || defaultMessage);
    }

    const redirectUrl = !isNullOrEmpty(options?.redirectUrl)
        ? options?.redirectUrl
        : terminationOptions.redirectUrl;

    const shouldRedirect =
        options?.terminationSurveyAction === TerminationAction.RedirectToUrl ||
        (options?.terminationSurveyAction !== TerminationAction.DisplayMessage &&
            !isNullOrEmpty(terminationOptions.redirectUrl));

    if (shouldRedirect) {
        let url = Piping.evaluateRecalls(interview, redirectUrl);
        if (!url.match(/^[a-zA-Z]+:\/\//)) {
            url = `http://${url}`;
        }
        window.location.assign(url);
    }

    return state;
};

const navigatePreviewQuestion = (
    state: NavigationState,
    questionnaire: QuestionnaireModel,
    interview: InterviewState,
    questionName: string,
): NavigationState => {
    const dest = getQuestionWithName(questionnaire, questionName);
    if (
        dest &&
        state.currentPage?.questions[0].index === dest.index &&
        state.blockIndex === dest.blockIndex
    )
        return state;
    let newState = branchToAbsolute(state, questionnaire, questionName);
    newState = buildPage(newState, interview);
    newState = {
        ...newState,
        currentPage: { ...newState.currentPage, previewQuestion: questionName },
    };
    return pageReady(newState);
};

const serverPageChanged = (
    state: NavigationState,
    interview: InterviewState,
    page: NavigationResultPageModel,
): NavigationState => {
    const newState = buildPageFromServer(state, interview, page);
    return pageReady(newState);
};

const updateCurrentPageQuestions = (
    state: NavigationState,
    interview: InterviewState,
    page: NavigationResultPageModel,
): NavigationState => {
    const newState = state;
    newState.currentPage.questions = getServerCurrentQuestions(page, interview.questionnaire);
    newState.currentPageOnServer = page;
    return newState;
};

export const navigationReducer = (
    state: NavigationState,
    action: ActionTypes,
    questionnaire: QuestionnaireModel,
    interview: InterviewState,
) => {
    switch (action.type) {
        case NAVIGATION_QUIT:
            return navigationQuit(
                state,
                interview,
                questionnaire,
                action.dispositionCode,
                action.options,
            );
        case NAVIGATION_DISPLAY_MESSAGE:
            return displayMessage(state, action.message);
        case SERVER_PAGE_CHANGED:
            return serverPageChanged(state, interview, action.page);
        case REFRESH_CURRENT_PAGE_QUESTIONS:
            return updateCurrentPageQuestions(state, interview, action.page);
        case SET_NAVIGATION_ENABLED:
            return setEnabled(state, action.enabled);
        default:
            break;
    }
    // If the current page on surver is unknown, use this reducer to determine the current page
    if (!state.currentPageOnServer) {
        switch (action.type) {
            case NAVIGATION_PAGE_READY:
                return pageReady(state);
            case NAVIGATION_COMPLETION_QUESTION_REACHED:
                return completionQuestionReached(state);
            case NAVIGATION_BRANCH_TO:
                return branchTo(state, questionnaire, action.options, interview);
            case NAVIGATION_NEXT_PAGE:
                return nextPage(state, interview);
            case NAVIGATION_PREVIOUS_PAGE:
                return previousPage(state, interview);
            case NAVIGATION_PREVIEW_QUESTION: {
                const question = getQuestionWithId(questionnaire, action.questionId);
                if (question) {
                    return navigatePreviewQuestion(state, questionnaire, interview, question.name);
                }
                break;
            }
            case NAVIGATION_RESET:
                return reset(state, questionnaire, interview);
            case NAVIGATION_SHORTCUT:
                return shortcut(state, action.blockName, action.exitToNextQuestion, interview);
            case SET_LOOP_ROW: {
                return setLoopRow(state, questionnaire, action.blockName.toUpperCase(), action.row);
            }
            default:
                break;
        }
    }
    return state;
};
